import { ContentData } from '@galaxy/email-ui/builder-v3';
import {
  Content,
  ContentBlock,
  EmailBlockType,
  EmailBuilderEmail,
  EmailBuilderTemplate,
  EmailColorSettings,
  EmailFooter,
  EmailLogoAlignment,
  EmailLogoType,
  EmailSettings,
  EmailSpacingSettings,
  EmailTopLogo,
  OwnerType,
} from '@vendasta/email-builder';
import {
  EmailBlockType as ProtoEmailBlockType,
  EmailLogoAlignment as ProtoEmailLogoAlignment,
  EmailLogoType as ProtoEmailLogoType,
} from '@vendasta/protos/emailbuilder/v1/enums';
import type {
  Content as ProtoContent,
  ContentBlock as ProtoContentBlock,
  EmailBuilderEmail as ProtoEmailBuilderEmail,
  EmailColorSettings as ProtoEmailColorSettings,
  EmailFooter as ProtoEmailFooter,
  EmailSettings as ProtoEmailSettings,
  EmailSpacingSettings as ProtoEmailSpacingSettings,
  EmailTopLogo as ProtoEmailTopLogo,
} from '@vendasta/protos/emailbuilder/v1/types';

// --- Generic helpers (no SDK dependency)

// Builder uses camelCase block types, backend renderer expects snake_case in stored JSON
const BUILDER_TO_STORED_TYPE: Record<string, string> = {
  richText: 'rich_text',
  contactCard: 'contact_card',
  imageAndText: 'image_and_text',
  simpleTable: 'simple_table',
};

const STORED_TO_BUILDER_TYPE: Record<string, string> = Object.fromEntries(
  Object.entries(BUILDER_TO_STORED_TYPE).map(([k, v]) => [v, k]),
);

// Mirrors email-builder-template-renderer's per-block padding defaults. `spacer` is omitted.
const DEFAULT_BLOCK_PADDING: Record<string, { top: number; bottom: number }> = {
  richText: { top: 10, bottom: 4 },
  image: { top: 23, bottom: 8 },
  heading: { top: 32, bottom: 0 },
  button: { top: 20, bottom: 8 },
  imageAndText: { top: 32, bottom: 8 },
  simpleTable: { top: 28, bottom: 8 },
  contactCard: { top: 32, bottom: 8 },
  divider: { top: 32, bottom: 16 },
  html: { top: 0, bottom: 0 },
};

function resolveBlockPadding(
  type: string,
  padding?: { top?: number; bottom?: number },
): { top: number; bottom: number } {
  const defaults = DEFAULT_BLOCK_PADDING[type] ?? { top: 0, bottom: 0 };
  return {
    top: padding?.top ?? defaults.top,
    bottom: padding?.bottom ?? defaults.bottom,
  };
}

export function serializeToEmailJson(data: ContentData): string {
  return JSON.stringify({
    subjectLine: data.subjectLine,
    previewText: data.previewText,
    emailSetup: data.contentSetup,
    emailContentBlocks: (data.contentBlocks || []).map((block) => ({
      ...block,
      type: BUILDER_TO_STORED_TYPE[block.type] || block.type,
    })),
    basicSetupValid: data.basicSetupValid,
  });
}

// --- Deprecated SDK helpers (@vendasta/email-builder npm package classes)

const LOGO_TYPE_MAP: Record<string, EmailLogoType> = {
  show: EmailLogoType.EMAIL_LOGO_TYPE_DEFAULT,
  custom: EmailLogoType.EMAIL_LOGO_TYPE_CUSTOM,
  hide: EmailLogoType.EMAIL_LOGO_TYPE_HIDDEN,
};

const LOGO_ALIGNMENT_MAP: Record<string, EmailLogoAlignment> = {
  left: EmailLogoAlignment.EMAIL_LOGO_ALIGNMENT_LEFT,
  center: EmailLogoAlignment.EMAIL_LOGO_ALIGNMENT_CENTER,
  right: EmailLogoAlignment.EMAIL_LOGO_ALIGNMENT_RIGHT,
};

const BLOCK_TYPE_MAP: Record<string, EmailBlockType> = {
  image: EmailBlockType.BLOCK_TYPE_IMAGE,
  richText: EmailBlockType.BLOCK_TYPE_RICH_TEXT,
  heading: EmailBlockType.BLOCK_TYPE_HEADING,
  button: EmailBlockType.BLOCK_TYPE_BUTTON,
  contactCard: EmailBlockType.BLOCK_TYPE_CONTACT_CARD,
  imageAndText: EmailBlockType.BLOCK_TYPE_IMAGE_AND_TEXT,
  simpleTable: EmailBlockType.BLOCK_TYPE_SIMPLE_TABLE,
  divider: EmailBlockType.BLOCK_TYPE_DIVIDER,
  spacer: EmailBlockType.BLOCK_TYPE_SPACER,
  html: EmailBlockType.BLOCK_TYPE_HTML,
};

const OWNER_TYPE_MAP: Record<string, OwnerType> = {
  [OwnerType.OWNER_TYPE_SYSTEM]: OwnerType.OWNER_TYPE_SYSTEM,
  [OwnerType.OWNER_TYPE_ACCOUNT_GROUP]: OwnerType.OWNER_TYPE_ACCOUNT_GROUP,
};

function mapLogoType(showLogo: string): EmailLogoType {
  return LOGO_TYPE_MAP[showLogo] || EmailLogoType.EMAIL_LOGO_TYPE_INVALID;
}

function mapLogoAlignment(align: string): EmailLogoAlignment {
  return LOGO_ALIGNMENT_MAP[align] || EmailLogoAlignment.EMAIL_LOGO_ALIGNMENT_INVALID;
}

/** @deprecated Use proto equivalent instead. */
export function mapBlockTypeToEnum(type: string): EmailBlockType {
  return BLOCK_TYPE_MAP[type] || EmailBlockType.BLOCK_TYPE_INVALID;
}

/** @deprecated Use proto equivalent instead. */
export function parseOwnerType(ownerTypeString: string | null): OwnerType {
  if (!ownerTypeString) {
    return OwnerType.OWNER_TYPE_SYSTEM;
  }
  return OWNER_TYPE_MAP[ownerTypeString] ?? OwnerType.OWNER_TYPE_SYSTEM;
}

/** @deprecated Use mapContentDataToProtoContent instead. */
export function mapToSdkContent(contentData: ContentData): Content {
  const topLogo = contentData.contentSetup?.topLogo;
  const footer = contentData.contentSetup?.footer;

  return new Content({
    subjectLine: contentData.subjectLine,
    previewText: contentData.previewText,
    topLogo: new EmailTopLogo({
      logoSettings: mapLogoType(topLogo?.showLogo),
      alignment: mapLogoAlignment(topLogo?.align),
      defaultSourceUrl: topLogo?.defaultSourceUrl,
      customImageUrl: topLogo?.customImageUrl,
      width: topLogo?.width,
      topPadding: topLogo?.topPadding,
      bottomPadding: topLogo?.bottomPadding,
      link: topLogo?.link,
    }),
    footer: new EmailFooter({
      disclaimer: footer?.disclaimer,
      text: footer?.text,
      showContactInfo: footer?.showContactInfo,
      showUnsubscribeLinks: footer?.showUnsubscribeLinks,
    }),
    emailContentBlocks: (contentData.contentBlocks || []).map(
      (block) =>
        new ContentBlock({
          type: mapBlockTypeToEnum(block.type),
          content: JSON.stringify(block.content),
          showPadding: block.showPadding,
          topPadding: block.padding?.top,
          bottomPadding: block.padding?.bottom,
        }),
    ),
  });
}

/** @deprecated Use mapContentDataToProtoEmailSettings instead. */
export function mapToSdkEmailSettings(contentData: ContentData): EmailSettings {
  const pageSetup = contentData.contentSetup?.pageSetup;
  const colors = contentData.contentSetup?.colors;

  return new EmailSettings({
    spacingSettings: new EmailSpacingSettings({
      outerWidth: pageSetup?.outerWidth,
      innerWidth: pageSetup?.innerWidth,
      innerHorizontalPadding: pageSetup?.innerHorizontalPadding,
      bottomContentPadding: pageSetup?.bottomContentPadding,
      topPadding: pageSetup?.topPadding,
    }),
    colorSettings: new EmailColorSettings({
      textColor: colors?.textColor,
      titleColor: colors?.titleColor,
      subtitleColor: colors?.subtitleColor,
      linkColor: colors?.linkColor,
      footerTextColor: colors?.footerTextColor,
      textBackground: colors?.textBackground,
      pageBackground: colors?.pageBackground,
    }),
  });
}

/** @deprecated Use mapProtoEmailToContentData instead. */
export function mapToContentData(email: EmailBuilderEmail): ContentData {
  if (!email?.emailJson) {
    return null;
  }
  const stored = JSON.parse(email.emailJson);
  return {
    id: email.emailBuilderEmailId,
    name: email.displayName,
    subjectLine: stored.subjectLine,
    previewText: stored.previewText,
    contentSetup: stored.emailSetup,
    contentBlocks: (stored.emailContentBlocks || []).map((block) => ({
      ...block,
      type: STORED_TO_BUILDER_TYPE[block.type] || block.type,
    })),
    basicSetupValid: stored.basicSetupValid,
  };
}

/** @deprecated Use mapProtoEmailToContentData instead. */
export function mapTemplateToContentData(template: EmailBuilderTemplate): ContentData {
  if (!template?.templateJson) {
    return null;
  }
  const stored = JSON.parse(template.templateJson);
  return {
    name: template.templateDisplayName,
    subjectLine: template.templateSubject || stored.subjectLine,
    previewText: stored.previewText,
    contentSetup: stored.emailSetup,
    contentBlocks: (stored.emailContentBlocks || []).map((block) => ({
      ...block,
      type: STORED_TO_BUILDER_TYPE[block.type] || block.type,
    })),
    basicSetupValid: stored.basicSetupValid,
  };
}

// --- Plain proto shapes (@vendasta/protos) for RenderEmail / createEmail; no npm SDK message classes.

const PROTO_LOGO_TYPE_MAP: Record<string, ProtoEmailLogoType> = {
  show: ProtoEmailLogoType.EMAIL_LOGO_TYPE_DEFAULT,
  custom: ProtoEmailLogoType.EMAIL_LOGO_TYPE_CUSTOM,
  hide: ProtoEmailLogoType.EMAIL_LOGO_TYPE_HIDDEN,
};

const PROTO_LOGO_ALIGNMENT_MAP: Record<string, ProtoEmailLogoAlignment> = {
  left: ProtoEmailLogoAlignment.EMAIL_LOGO_ALIGNMENT_LEFT,
  center: ProtoEmailLogoAlignment.EMAIL_LOGO_ALIGNMENT_CENTER,
  right: ProtoEmailLogoAlignment.EMAIL_LOGO_ALIGNMENT_RIGHT,
};

const PROTO_BLOCK_TYPE_MAP: Record<string, ProtoEmailBlockType> = {
  image: ProtoEmailBlockType.BLOCK_TYPE_IMAGE,
  richText: ProtoEmailBlockType.BLOCK_TYPE_RICH_TEXT,
  heading: ProtoEmailBlockType.BLOCK_TYPE_HEADING,
  button: ProtoEmailBlockType.BLOCK_TYPE_BUTTON,
  contactCard: ProtoEmailBlockType.BLOCK_TYPE_CONTACT_CARD,
  imageAndText: ProtoEmailBlockType.BLOCK_TYPE_IMAGE_AND_TEXT,
  simpleTable: ProtoEmailBlockType.BLOCK_TYPE_SIMPLE_TABLE,
  divider: ProtoEmailBlockType.BLOCK_TYPE_DIVIDER,
  spacer: ProtoEmailBlockType.BLOCK_TYPE_SPACER,
  html: ProtoEmailBlockType.BLOCK_TYPE_HTML,
};

function mapProtoLogoType(showLogo: string | undefined): ProtoEmailLogoType {
  return (showLogo && PROTO_LOGO_TYPE_MAP[showLogo]) || ProtoEmailLogoType.EMAIL_LOGO_TYPE_INVALID;
}

function mapProtoLogoAlignment(align: string | undefined): ProtoEmailLogoAlignment {
  return (align && PROTO_LOGO_ALIGNMENT_MAP[align]) || ProtoEmailLogoAlignment.EMAIL_LOGO_ALIGNMENT_INVALID;
}

function mapBlockTypeToProtoEnum(type: string): ProtoEmailBlockType {
  return PROTO_BLOCK_TYPE_MAP[type] || ProtoEmailBlockType.BLOCK_TYPE_INVALID;
}

export function mapProtoEmailToContentData(email: ProtoEmailBuilderEmail): ContentData {
  if (!email?.emailJson) {
    return null;
  }
  const stored = JSON.parse(email.emailJson);
  return {
    id: email.emailBuilderEmailId,
    name: email.displayName,
    subjectLine: stored.subjectLine,
    previewText: stored.previewText,
    contentSetup: stored.emailSetup,
    contentBlocks: (stored.emailContentBlocks || []).map((block) => ({
      ...block,
      type: STORED_TO_BUILDER_TYPE[block.type] || block.type,
    })),
    basicSetupValid: stored.basicSetupValid,
    locale: email.locale,
  };
}

/** Maps builder UI state to proto `Content` for `renderEmail$` / similar RPCs (plain objects, not npm SDK classes). */
export function mapContentDataToProtoContent(contentData: ContentData): ProtoContent {
  const topLogo = contentData.contentSetup?.topLogo;
  const footer = contentData.contentSetup?.footer;

  const topLogoMsg: ProtoEmailTopLogo = {
    logoSettings: mapProtoLogoType(topLogo?.showLogo),
    alignment: mapProtoLogoAlignment(topLogo?.align),
    defaultSourceUrl: topLogo?.defaultSourceUrl ?? '',
    customImageUrl: topLogo?.customImageUrl ?? '',
    width: topLogo?.width ?? 0,
    topPadding: topLogo?.topPadding ?? 0,
    bottomPadding: topLogo?.bottomPadding ?? 0,
    link: topLogo?.link ?? '',
  };

  const footerMsg: ProtoEmailFooter = {
    disclaimer: footer?.disclaimer ?? '',
    text: footer?.text ?? '',
    showContactInfo: footer?.showContactInfo ?? false,
    showUnsubscribeLinks: footer?.showUnsubscribeLinks ?? false,
  };

  const emailContentBlocks: ProtoContentBlock[] = (contentData.contentBlocks || []).map((block) => {
    const padding = resolveBlockPadding(block.type, block.padding);
    return {
      type: mapBlockTypeToProtoEnum(block.type),
      content: JSON.stringify(block.content),
      showPadding: block.showPadding ?? false,
      topPadding: padding.top,
      bottomPadding: padding.bottom,
    };
  });

  return {
    subjectLine: contentData.subjectLine ?? '',
    previewText: contentData.previewText ?? '',
    topLogo: topLogoMsg,
    footer: footerMsg,
    emailContentBlocks,
  };
}

/** Maps builder UI state to proto `EmailSettings` for `renderEmail$` (plain objects). */
export function mapContentDataToProtoEmailSettings(contentData: ContentData): ProtoEmailSettings {
  const pageSetup = contentData.contentSetup?.pageSetup;
  const colors = contentData.contentSetup?.colors;

  const spacingSettings: ProtoEmailSpacingSettings = {
    outerWidth: pageSetup?.outerWidth ?? 0,
    innerWidth: pageSetup?.innerWidth ?? 0,
    innerHorizontalPadding: pageSetup?.innerHorizontalPadding ?? 0,
    bottomContentPadding: pageSetup?.bottomContentPadding ?? 0,
    topPadding: pageSetup?.topPadding ?? 0,
  };

  const colorSettings: ProtoEmailColorSettings = {
    textColor: colors?.textColor ?? '',
    titleColor: colors?.titleColor ?? '',
    subtitleColor: colors?.subtitleColor ?? '',
    linkColor: colors?.linkColor ?? '',
    footerTextColor: colors?.footerTextColor ?? '',
    textBackground: colors?.textBackground ?? '',
    pageBackground: colors?.pageBackground ?? '',
  };

  return {
    spacingSettings,
    colorSettings,
  };
}
